import {
  sendMessageToHost,
  sendMessageToHostWithCompletion,
} from "~/utils/events";
import { isBrowser } from "~/utils/helpers";

export interface SheetModalInstance {
  id: string;
  isOpen: boolean;
}

export class SheetModalCoordinator {
  private instances = new Map<string, SheetModalInstance>();

  private _isShowing = false;

  private get isShowing() {
    return this._isShowing;
  }

  private set isShowing(isShowing) {
    const didChange = isShowing !== this._isShowing;

    this._isShowing = isShowing;

    if (!didChange) return;

    const backgroundContentElement: HTMLElement | null = document.querySelector(
      `[data-sheet-modal="lock-padding"]`,
    );

    // HACK: Prevent the content under the sheet modal from shifting (due to safe area inset changes)
    // by setting the padding to the current safa area value before calling `setTabBarVisible`
    if (backgroundContentElement && isShowing) {
      backgroundContentElement.style.paddingBottom = window.getComputedStyle(
        backgroundContentElement,
      ).paddingBottom;
    }

    sendMessageToHostWithCompletion({
      ui: {
        setTabBarVisible: {
          animated: true,
          visible: !isShowing,
        },
      },
    }).then(() => {
      if (backgroundContentElement && !isShowing) {
        // Undo the above hack once no more sheet modals are showing
        // NOTE: This needs to be run after `setTabBarVisible` has completed
        backgroundContentElement.style.paddingBottom = "";
      }
    });

    sendMessageToHost({
      ui: {
        setScrollingEnabled: {
          enabled: !isShowing,
        },
      },
    });

    sendMessageToHost({
      ui: {
        setInteractiveDismiss: {
          disabled: isShowing,
        },
      },
    });
  }

  lockScrolling(shouldLock: boolean) {
    const node = document.querySelector("body") as HTMLBodyElement;

    if (shouldLock) {
      node.style.overflow = "hidden";
    } else {
      node.style.removeProperty("overflow");
    }
  }

  handlePropsChange(instance: SheetModalInstance, isMounted = true) {
    if (isMounted) {
      if (instance.isOpen) {
        this.instances.set(instance.id, instance);
      } else {
        this.instances.delete(instance.id);
      }
    } else {
      this.instances.delete(instance.id);
    }

    this.isShowing = this.instances.size > 0;

    if (isBrowser()) {
      this.lockScrolling(this.isShowing && isMounted);
    }
  }

  static shared = new SheetModalCoordinator();
}
